import { useEffect } from 'react';
import { useReducedMotion } from 'framer-motion';

// Extend the original restrained fade-up to plain section headings on every route.
// Do not touch existing Framer-managed elements, tab panels, layout dimensions or scroll.
export const useSectionMotion = pathname => {
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    if (reducedMotion || !window.IntersectionObserver || !Element.prototype.animate) return;
    const root = document.querySelector('.site-main');
    if (!root) return;
    const seen = new WeakSet();
    const animations = new Set();
    let scanFrame;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        observer.unobserve(target);
        if (target.closest('[inert], [aria-hidden="true"]')) return;
        const animation = target.animate([
          { opacity: 0.35, transform: 'translateY(16px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ], { duration: 620, easing: 'cubic-bezier(0.22,1,0.36,1)' });
        animations.add(animation);
        animation.onfinish = () => animations.delete(animation);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -24px 0px' });
    const scan = () => {
      root.querySelectorAll('h1, section h2, .en-section-title').forEach(element => {
        if (seen.has(element) || element.closest('.stable-panels, [data-motion-managed], [inert], [role="dialog"]')) return;
        seen.add(element);
        if (element.style.opacity || element.style.transform) return;
        observer.observe(element);
      });
    };
    scan();
    const mutations = new MutationObserver(() => { cancelAnimationFrame(scanFrame); scanFrame = requestAnimationFrame(scan); });
    mutations.observe(root, { childList: true, subtree: true });
    return () => { cancelAnimationFrame(scanFrame); mutations.disconnect(); observer.disconnect(); animations.forEach(animation => animation.cancel()); };
  }, [pathname, reducedMotion]);
};